import { lerp } from '../core/math';
import { Camera } from './camera';
import { TrackRenderer } from './trackRenderer';
import { drawKart } from './entities';
import type { RenderWorld, Viewport, WorldFrame, WorldRenderer } from './renderer';
import type { OverheadOrientation } from '../storage/settings';

/**
 * Speed-trail ramp, slow to fast. Mirrors three of the app's own `:root`
 * tokens (`--info`, `--pace-yellow`, `--accent`) rather than a bespoke `hsl()`
 * rainbow, so the trail reads as part of the same palette as the rest of the
 * UI. A canvas duplicate rather than a live CSS read — nothing else in the
 * canvas layer reads computed styles yet (that is Stage 2 of the UI revamp,
 * `docs/ui-revamp-prompt.md`) — but keeping the same three hex values means
 * this stops being a duplicate the day that stage lands. None of the three
 * are red or green, so — unlike the old scale — this needs no colourblind
 * variant of its own.
 */
const TRAIL_SLOW: [number, number, number] = [0x4d, 0x9b, 0xe6]; // --info
const TRAIL_MID: [number, number, number] = [0xe8, 0xc1, 0x4a]; // --pace-yellow
const TRAIL_FAST: [number, number, number] = [0xff, 0x8a, 0x3d]; // --accent

function trailColor(t: number): string {
  const [a, b, f] = t < 0.5 ? [TRAIL_SLOW, TRAIL_MID, t * 2] : [TRAIL_MID, TRAIL_FAST, (t - 0.5) * 2];
  const r = Math.round(lerp(a[0], b[0], f));
  const g = Math.round(lerp(a[1], b[1], f));
  const bl = Math.round(lerp(a[2], b[2], f));
  return `rgb(${r},${g},${bl})`;
}

/**
 * The original renderer, unchanged in behaviour — just moved behind
 * `WorldRenderer` so main.ts can hold either this or the Three.js one without
 * caring which. This is also the WebGL-unavailable fallback.
 */
export class Canvas2DRenderer implements WorldRenderer {
  private readonly camera = new Camera();
  private trackRenderer: TrackRenderer | null = null;
  private colorblind = false;
  /** Fixed per-instance angle offset for the speed streaks, so they fade in
   *  and out at stable positions rather than jittering every frame. */
  private readonly streakSeed = Math.random() * Math.PI * 2;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly ctx: CanvasRenderingContext2D,
  ) {}

  get viewport(): Viewport {
    return { viewW: this.camera.viewW, viewH: this.camera.viewH, dpr: this.camera.dpr };
  }

  resize(): void {
    this.camera.resize(this.canvas);
  }

  setWorld(world: RenderWorld): void {
    this.trackRenderer = new TrackRenderer(world.track, world.corners, world.profile, world.weather);
    this.trackRenderer.setPalette(this.colorblind);
  }

  setPalette(colorblind: boolean): void {
    this.colorblind = colorblind;
    this.trackRenderer?.setPalette(colorblind);
  }

  /** The 2D camera only ever looks straight down; nothing to switch. */
  setCameraMode(): void {}

  setOverheadOrientation(orient: OverheadOrientation): void {
    this.camera.setOrientation(orient === 'kart-up');
  }

  follow(x: number, y: number, heading: number, speed: number, dt: number): void {
    this.camera.follow(x, y, heading, speed, dt);
  }

  snap(x: number, y: number, heading: number): void {
    this.camera.snap(x, y, heading);
  }

  draw(f: WorldFrame): void {
    const ctx = this.ctx;
    const cam = this.camera;
    const tr = this.trackRenderer;
    if (!tr) return;

    tr.drawGrass(ctx, cam);
    tr.draw(ctx, cam, f.referenceLine);
    if (f.effects) f.effects.draw(ctx, cam);

    ctx.save();
    cam.applyTransform(ctx);

    if (f.trail) this.drawTrail(f.trail);

    if (f.ghost) {
      drawKart(
        ctx,
        { x: f.ghost.x, y: f.ghost.y, heading: f.ghost.heading, steer: 0 },
        { ...f.kart.livery, body: '#7f8794', accent: '#aeb6c2', helmet: '#5d6673' },
        0.42,
      );
    }

    // Solid AI karts render before the player's own, so the player's kart is
    // never hidden underneath a rival it happens to be alongside.
    for (const rival of f.rivals) drawKart(ctx, rival, rival.livery);

    drawKart(ctx, f.kart, f.kart.livery, 1, f.kart.damage ?? 1);
    ctx.restore();

    if (f.speedFx) this.drawSpeedFx(f.speedFx);
  }

  /**
   * Edge vignette and radial speed streaks, screen-space so they hold steady
   * under both Track-up and Kart-up. Drawn centred on the kart (always screen
   * centre) rather than tied to heading, which is what keeps them valid in
   * either orientation with no extra bookkeeping. `t` already carries the
   * reduced-motion scale-down from the caller.
   */
  private drawSpeedFx(t: number): void {
    const ctx = this.ctx;
    const cam = this.camera;
    const w = cam.viewW;
    const h = cam.viewH;
    const cx = w / 2;
    const cy = h / 2;
    const r = Math.hypot(w, h) / 2;

    ctx.save();
    ctx.setTransform(cam.dpr, 0, 0, cam.dpr, 0, 0);

    const vignette = ctx.createRadialGradient(cx, cy, r * 0.45, cx, cy, r);
    vignette.addColorStop(0, 'rgba(0,0,0,0)');
    vignette.addColorStop(1, `rgba(0,0,0,${0.38 * t})`);
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);

    const count = Math.round(10 * t);
    if (count > 0) {
      ctx.strokeStyle = `rgba(255,255,255,${0.22 * t})`;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      const inner = r * 0.62;
      const outer = r * (0.85 + 0.15 * t);
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2 + this.streakSeed;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
        ctx.lineTo(cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /**
   * The line already driven, coloured by speed and fading behind the kart.
   * `vMin`/`vMax` are taken across the whole ghost, not just the visible
   * window, so the colour scale does not jump as more of the lap comes into
   * view.
   */
  private drawTrail(trail: WorldFrame['trail']): void {
    if (!trail) return;
    const { x, y, speed, from, to } = trail;
    if (to < 2) return;
    const ctx = this.ctx;

    let vMin = Infinity;
    let vMax = 0;
    for (let i = 0; i < speed.length; i++) {
      vMin = Math.min(vMin, speed[i]);
      vMax = Math.max(vMax, speed[i]);
    }

    ctx.lineWidth = 0.55;
    ctx.lineCap = 'round';
    for (let i = from; i < to; i++) {
      const t = (speed[i] - vMin) / Math.max(1e-6, vMax - vMin);
      const age = (i - from) / Math.max(1, to - from);
      ctx.globalAlpha = 0.15 + age * 0.6;
      ctx.strokeStyle = trailColor(t);
      ctx.beginPath();
      ctx.moveTo(x[i], y[i]);
      ctx.lineTo(x[i + 1], y[i + 1]);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  dispose(): void {
    // Path2D objects and the 2D context need no explicit teardown.
  }
}
